/**
 * PDF Inbox — the parser triage queue, as a master/detail two-pane surface.
 *
 * Master pane (left, fixed width): the list of PDFs awaiting review. Detail
 * pane (right, flex): the selected PDF's blocks, rendered by the route's
 * <Outlet> child. Selection lives in the URL (/inbox/:dailyPdfId), not local
 * state — so it's deep-linkable and survives refresh/back/forward.
 *
 * This page owns the inbox list + PCO data + the PDF preview modal, and hands
 * them to the detail route via <Outlet context>. Below 1024px the panes
 * collapse to a single column (CSS); selecting a PDF swaps to a full-width
 * detail view with a back button.
 */
import { useState } from 'react';
import { Outlet, useMatch } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { useInbox } from '../lib/useInbox';
import { useSettingsPcos } from '../lib/useSettings';
import { PdfInboxList } from '../components/inbox/PdfInboxList';
import { PdfPreviewModal } from '../components/PdfPreviewModal';
import { FailedPdfsBanner } from '../components/FailedPdfsBanner';
import { ForemanCoverageBanner } from '../components/ForemanCoverageBanner';

// Coverage banner kill switch — see the comment at the render site.
const SHOW_COVERAGE_BANNER = false;
import type { InboxOutletContext } from '../components/inbox/InboxPdfDetail';

export function DashboardPage() {
  const { me } = useAuth();
  // PCO + Foreman filters for the master list — component state only, never
  // persisted, so they can't silently hide PDFs on a later visit. The two
  // compose (AND): both ride the same useInbox call/request.
  const [pcoFilter, setPcoFilter] = useState<string | null>(null);
  const [foremanFilter, setForemanFilter] = useState<string | null>(null);
  // The skipped-reports disclosure at the foot of the list. Collapsed on every
  // load, deliberately and without persistence — finished reports are history,
  // not the work queue, and a fresh visit should open on the work.
  const [showSkipped, setShowSkipped] = useState(false);
  const inbox = useInbox({
    pcoId: pcoFilter,
    foreman: foremanFilter,
    includeSkipped: showSkipped,
  });
  const pcoQuery = useSettingsPcos();
  const [previewPdfId, setPreviewPdfId] = useState<string | null>(null);

  const match = useMatch('/inbox/:dailyPdfId');
  const selectedId = match?.params.dailyPdfId ?? null;

  if (!me) return null;

  const outletContext: InboxOutletContext = {
    pcos: pcoQuery.data ?? [],
    refetchInbox: async () => {
      await inbox.refetch();
    },
    openPdfPreview: setPreviewPdfId,
    // null until the list has actually loaded — the empty detail pane must
    // never flash "all caught up" while the answer is still in flight.
    //
    // Counts rows with WORK LEFT, not rows in the list. Since 2026-08-03 the
    // list also carries fully-processed dailies whose independent re-read is
    // still flagged (so the flag stays reachable); those are not review work
    // and must not keep the pane from saying "all caught up".
    pdfsPending: inbox.data ? inbox.data.pdfs.filter((p) => p.blocks_pending > 0).length : null,
  };

  return (
    <>
      {/* Full-height column: the two intake banners on top, inbox fills the
          rest. They must be visible on landing, but `.inbox-shell` is
          height:100vh — so we cap the column at 100vh and let the shell flex
          into the remaining space instead of overflowing the viewport.

          ORDER: reports that arrived and went wrong (failed / warned parses)
          come first, then reports that never arrived at all (coverage). The
          failed banner is loud and rare; coverage is a standing roll-call and
          in the healthy case is a single quiet line, so it reads better as the
          last thing above the list. */}
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <FailedPdfsBanner />
        {/* Hidden per Brandon 2026-08-02 until the two-tier logic earns trust
            against the real forwarding cadence. The cron + ops emails still
            run (8+ site-day threshold). Flip to true to restore the banner. */}
        {SHOW_COVERAGE_BANNER && <ForemanCoverageBanner />}
        <div
          className="inbox-shell"
          data-detail-open={selectedId ? 'true' : 'false'}
          style={{ height: 'auto', flex: '1 1 auto', minHeight: 0 }}
        >
          <div className="inbox-master">
            <PdfInboxList
              pdfs={inbox.data?.pdfs ?? []}
              isLoading={inbox.isLoading}
              error={inbox.error}
              selectedId={selectedId}
              pcoFacets={inbox.pcoFacets}
              unassignedCount={inbox.unassignedCount}
              foremanFacets={inbox.foremanFacets}
              selectedPcoId={pcoFilter}
              selectedForeman={foremanFilter}
              onSelectPco={setPcoFilter}
              onSelectForeman={setForemanFilter}
              skippedCount={inbox.skippedCount}
              skippedPdfs={inbox.skippedPdfs}
              showSkipped={showSkipped}
              onToggleSkipped={() => setShowSkipped((v) => !v)}
            />
          </div>
          <div className="inbox-detail">
            <Outlet context={outletContext} />
          </div>
        </div>
      </div>

      <PdfPreviewModal source={previewPdfId} onClose={() => setPreviewPdfId(null)} />
    </>
  );
}
